import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Compromiso, PropuestaDetail } from '@/types';
import { useUIStore } from '@/store/uiStore';
import {
  compromisosVisibles,
  compromisoEstaSuspendido,
  healthCompromiso,
} from '@/lib/propuesta-selectors';
import { useEstadoChange } from '@/hooks/useEstadoChange';
import {
  listArchivados,
  listTags,
  restoreCompromiso,
  restoreHito,
} from '@/api/clientes';
import { EstadoSelect } from './EstadoSelect';
import { EstadoModal } from './EstadoModal';
import { EventosHistorial } from './EventosHistorial';
import { EditCompromisoDrawer } from './EditCompromisoDrawer';
import styles from './facility-tabs.module.css';

interface Props {
  detail: PropuestaDetail;
  tagsById: Map<string, { nombre: string; color: string }>;
}

export function CompromisosView({ detail, tagsById }: Props) {
  const lente = useUIStore((s) => s.lente);
  const scope = useUIStore((s) => s.scope);
  const propuestaId = detail.propuesta.id;
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [iniFilter, setIniFilter] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [drawer, setDrawer] = useState<{ open: boolean; compromiso: Compromiso | null }>({
    open: false,
    compromiso: null,
  });
  const [archivadosOpen, setArchivadosOpen] = useState(false);

  // Tags completos para el drawer
  const { data: tagsAll = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: listTags,
    staleTime: 5 * 60 * 1000,
  });

  // Archivados (compromisos + items individuales)
  const { data: archivados } = useQuery({
    queryKey: ['propuesta-archivados', propuestaId],
    queryFn: () => listArchivados(propuestaId),
    enabled: archivadosOpen,
  });

  const mRestoreCompromiso = useMutation({
    mutationFn: (id: string) => restoreCompromiso(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['propuesta-detail', propuestaId] });
      qc.invalidateQueries({ queryKey: ['propuesta-archivados', propuestaId] });
    },
  });
  const mRestoreHito = useMutation({
    mutationFn: (id: string) => restoreHito(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['propuesta-detail', propuestaId] });
      qc.invalidateQueries({ queryKey: ['propuesta-archivados', propuestaId] });
    },
  });

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibles = useMemo(
    () => compromisosVisibles(detail, scope, lente),
    [detail, scope, lente],
  );

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    const list = visibles.filter((c) => {
      if (iniFilter && c.iniciativaId !== iniFilter) return false;
      if (!term) return true;
      return (
        c.nombre.toLowerCase().includes(term) ||
        (c.descripcion || '').toLowerCase().includes(term) ||
        c.codigo.toLowerCase().includes(term)
      );
    });
    // Orden por código ascendente, natural sort (P1, P2, ..., P10, P11)
    const collator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });
    return [...list].sort((a, b) => collator.compare(a.codigo, b.codigo));
  }, [visibles, search, iniFilter]);

  const iniById = Object.fromEntries(detail.iniciativas.map((i) => [i.id, i]));

  const { change, pending, submit, cancel, isSubmitting } = useEstadoChange(propuestaId);

  return (
    <>
      <div className={styles.toolbar}>
        <h2 className={styles.toolbarTitle}>Compromisos · {filtered.length} partidas</h2>
        <div className={styles.filters}>
          <input
            type="search"
            placeholder="Buscar partida…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.input}
          />
          <select
            className={styles.select}
            value={iniFilter}
            onChange={(e) => setIniFilter(e.target.value)}
          >
            <option value="">Todas las iniciativas</option>
            {detail.iniciativas.map((i) => (
              <option key={i.id} value={i.id}>
                {i.codigo} · {i.nombre}
              </option>
            ))}
          </select>
          <button
            className={styles.clearBtn}
            onClick={() => {
              setSearch('');
              setIniFilter('');
            }}
          >
            Limpiar
          </button>
          <button
            className={styles.clearBtn}
            onClick={() => setDrawer({ open: true, compromiso: null })}
            style={{
              background: 'var(--brand)',
              color: 'white',
              borderColor: 'var(--brand)',
              fontWeight: 700,
            }}
          >
            + Nueva partida
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filtered.length === 0 && (
          <div className="empty">No hay compromisos que coincidan con los filtros.</div>
        )}
        {filtered.map((c) => {
          const hisCompr = detail.hitos.filter(
            (h) => h.parentType === 'compromiso' && h.parentId === c.id,
          );
          // R4: suspendidos no cuentan en el total ni en el porcentaje del compromiso
          const activos = hisCompr.filter((h) => h.estado !== 'suspendido');
          const entregadosN = activos.filter((h) => h.estado === 'entregado').length;
          const totalN = activos.length;
          const pct = totalN ? Math.round((entregadosN / totalN) * 100) : 0;
          const ini = iniById[c.iniciativaId];
          const estaSuspendido = compromisoEstaSuspendido(detail, c.id);
          const health = healthCompromiso(detail, c.id);
          const healthCls =
            health === 'red'
              ? styles.cardBloqueado
              : health === 'yellow'
                ? styles.cardHealthYellow
                : health === 'green'
                  ? styles.cardHealthGreen
                  : '';

          return (
            <div
              key={c.id}
              id={`compromiso-${c.id}`}
              style={{ scrollMarginTop: 80 }}
              className={`${styles.compromisoCard} ${estaSuspendido ? styles.cardSuspendido : healthCls}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className={styles.iniTag}>{c.codigo}</span>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{c.nombre}</span>
                  </div>
                  {ini && (
                    <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 4, fontWeight: 600 }}>
                      Iniciativa: {ini.codigo} · {ini.nombre}
                    </div>
                  )}
                  {c.descripcion && (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                      {c.descripcion}
                    </div>
                  )}
                  {c.tagIds.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                      {c.tagIds.map((tid) => {
                        const t = tagsById.get(tid);
                        if (!t) return null;
                        return (
                          <span
                            key={tid}
                            style={{
                              background: t.color + '22',
                              color: t.color,
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '.04em',
                            }}
                          >
                            {t.nombre}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', minWidth: 100, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => setDrawer({ open: true, compromiso: c })}
                    aria-label={`Editar partida ${c.codigo}`}
                    title="Editar partida"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      width: 28,
                      height: 28,
                      fontSize: 14,
                      cursor: 'pointer',
                      color: 'var(--text-muted)',
                      lineHeight: 1,
                    }}
                  >
                    ✎
                  </button>
                  <div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--brand)' }}>{pct}%</div>
                    <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                      {entregadosN} / {totalN} ítems
                    </div>
                  </div>
                </div>
              </div>

              {hisCompr.length > 0 && (
                <details style={{ marginTop: 12 }}>
                  <summary
                    style={{
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-muted)',
                      padding: '4px 0',
                    }}
                  >
                    Items / Equipamiento ({hisCompr.length})
                  </summary>
                  <div className={styles.itemList}>
                    {hisCompr.map((h) => {
                      const isBloqueado = h.estado === 'bloqueado';
                      const isSuspendido = h.estado === 'suspendido';
                      const isEntregado = h.estado === 'entregado';
                      const isExpanded = expanded.has(h.id);
                      return (
                        <div key={h.id}>
                          <div
                            className={[
                              styles.itemRow,
                              isEntregado ? styles.itemRowDone : '',
                              isBloqueado ? styles.itemRowBloqueado : '',
                              isSuspendido ? styles.itemRowSuspendido : '',
                            ].join(' ')}
                          >
                            <div className={styles.itemEstadoWrap}>
                              <EstadoSelect
                                value={h.estado}
                                disabled={isSubmitting}
                                onChange={(next) =>
                                  change({
                                    hitoId: h.id,
                                    hitoTitulo: h.titulo,
                                    prevEstado: h.estado,
                                    nextEstado: next,
                                  })
                                }
                                ariaLabel={`Estado de ${h.titulo}`}
                              />
                            </div>
                            <span className={styles.itemTitulo}>{h.titulo}</span>
                            <button
                              type="button"
                              className={styles.historialBtn}
                              onClick={() => toggleExpand(h.id)}
                              aria-label={`Historial de ${h.titulo}`}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? '▾' : '▸'} historial
                            </button>
                          </div>
                          {isExpanded && <EventosHistorial hitoId={h.id} />}
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>

      {/* Sección Archivados — colapsable, se carga on-demand */}
      <div style={{ marginTop: 20, borderTop: '1px dashed var(--border)', paddingTop: 14 }}>
        <button
          type="button"
          onClick={() => setArchivadosOpen(!archivadosOpen)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--text-soft)',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '.06em',
            padding: '4px 0',
          }}
        >
          {archivadosOpen ? '▾' : '▸'} Archivados
          {archivados && (
            <> ({archivados.compromisos.length} partidas, {archivados.items.length} ítems)</>
          )}
        </button>

        {archivadosOpen && archivados && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {archivados.compromisos.length === 0 && archivados.items.length === 0 && (
              <div className="empty">No hay partidas ni ítems archivados.</div>
            )}

            {archivados.compromisos.map((c) => (
              <div
                key={c.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  background: 'rgba(148,163,184,0.08)',
                  border: '1px dashed var(--border)',
                  borderRadius: 10,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-soft)', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>
                    Partida · archivada el {new Date(c.deletedAt || '').toLocaleDateString('es-CL')}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>
                    {c.codigo} · {c.nombre}
                  </div>
                </span>
                <button
                  type="button"
                  onClick={() => mRestoreCompromiso.mutate(c.id)}
                  disabled={mRestoreCompromiso.isPending}
                  style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    color: 'var(--brand)',
                  }}
                >
                  ↺ Restaurar
                </button>
              </div>
            ))}

            {archivados.items.map((it) => (
              <div
                key={it.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 14px',
                  background: 'rgba(148,163,184,0.05)',
                  border: '1px dashed var(--border)',
                  borderRadius: 8,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                    Ítem · archivado el {new Date(it.deletedAt || '').toLocaleDateString('es-CL')}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{it.titulo}</div>
                </span>
                <button
                  type="button"
                  onClick={() => mRestoreHito.mutate(it.id)}
                  disabled={mRestoreHito.isPending}
                  style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    color: 'var(--brand)',
                  }}
                >
                  ↺
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <EstadoModal
        open={!!pending}
        hitoTitulo={pending?.hitoTitulo ?? ''}
        estadoAnterior={pending?.prevEstado ?? null}
        estadoNuevo={pending?.nextEstado ?? null}
        onCancel={cancel}
        onSubmit={submit}
        submitting={isSubmitting}
      />

      <EditCompromisoDrawer
        open={drawer.open}
        compromiso={drawer.compromiso}
        detail={detail}
        tags={tagsAll}
        onClose={() => setDrawer({ open: false, compromiso: null })}
      />
    </>
  );
}
